import { ChangeEvent, ComponentType, useRef } from 'react';

import KeyboardReact from 'react-simple-keyboard';

import { withProps } from 'utils/with-props';

import { IFieldHandlers, IVirtualKeyboardHandlers } from '../types';

export function useVirtualKeyboard() {
  const keyboard = useRef<KeyboardReact>();
  const activeField = useRef('');
  const formValues = useRef({});
  const formHandlers = useRef<{ [key: string]: IFieldHandlers }>({});

  const changeFieldValue = (field: string) => (input: string) => {
    formValues.current[field] = input;
  };

  const changeCurrentFieldValue = (input: string) => changeFieldValue(activeField.current)(input);

  const syncCurrentFieldValue = (input: string) => {
    const handlers = formHandlers.current[activeField.current];
    if (handlers) {
      handlers.setFieldValue(activeField.current, input);
    }
  };

  const attachFormHandlers = (field: string) => (handlers: IFieldHandlers) => {
    formHandlers.current[field] = handlers;
  };

  const updateKeyboard = (value: string) => {
    if (keyboard.current) {
      keyboard.current.setInput(value);
    }
  };

  const setActiveField = (field: string) => {
    return () => {
      activeField.current = field;
      updateKeyboard(formValues.current[field]);
    };
  };

  const attachProvider = (ref: KeyboardReact) => {
    keyboard.current = ref;
  };

  const createField = (name: string, initialValue = '') => {
    formValues.current[name] = initialValue;
    const changeValue = changeFieldValue(name);
    const setActive = setActiveField(name);
    const onChange = (change: ChangeEvent<{ value: string }>) => {
      const handlers = formHandlers.current[name];
      const value = change.target.value;

      if (handlers) {
        handlers.handleChange(change);
      }
      changeValue(value);
      updateKeyboard(value);
    };

    return {
      name,
      attachFormHandlers: attachFormHandlers(name),
      changeValue,
      setActive,
      onChange,
      componentWithKeyboard: <P extends IVirtualKeyboardHandlers>(component: ComponentType<P>) =>
        withProps<IVirtualKeyboardHandlers, P>(component, {
          onFocus: setActive,
          onChange
        })
    };
  };

  const keyboardOnChange = (input: string) => {
    const field = activeField.current;
    if (field) {
      changeCurrentFieldValue(input);
      syncCurrentFieldValue(input);
    }
  };

  return {
    attachProvider,
    createField,
    keyboardOnChange
  };
}
